// ABOUTME: Custom tooltip component with click/hover triggers and smart positioning
// ABOUTME: Supports auto-flip when not enough space and follows scroll in parent containers

import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useZIndex } from "@shared/hooks/useZIndex"

type Placement = "top" | "right" | "bottom" | "left"
type Trigger = "click" | "hover"
type Theme = "dark" | "light"

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  placement?: Placement
  className?: string
  tooltipClassName?: string
  arrow?: boolean
  delay?: number
  interactive?: boolean
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  trigger?: Trigger
  theme?: Theme
  maxWidth?: string
  closeOnScroll?: boolean
  disabled?: boolean
}

const CTooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement: placementProp,
  className = "",
  tooltipClassName = "",
  arrow = true,
  delay = 100,
  interactive: _interactive = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  trigger: triggerProp,
  theme = "dark",
  maxWidth = "min(300px, 90vw)",
  closeOnScroll = true,
  disabled = false,
}) => {
  // Определяем touch-устройство (без контекстов)
  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)

  // Умолчания зависят от типа устройства
  const placement = placementProp ?? (isTouchDevice ? "bottom" : "right")
  const trigger = triggerProp ?? (isTouchDevice ? "click" : "hover")
  // Используем контролируемое или внутреннее состояние
  const [internalVisible, setInternalVisible] = useState(false)
  const visible = controlledIsOpen !== undefined ? controlledIsOpen : internalVisible

  const setVisible = (newValue: boolean) => {
    if (controlledIsOpen !== undefined && onOpenChange) {
      // Для контролируемого компонента вызываем callback
      onOpenChange(newValue)
    } else {
      // Для неконтролируемого компонента используем внутреннее состояние
      setInternalVisible(newValue)
    }
  }

  // The outside-click and scroll listeners live in an effect keyed on visibility; closing
  // must still reach the onOpenChange the parent passes NOW, so the effect does not
  // re-subscribe (and re-arm its 50 ms delay) on every render.
  const closeFromOutside = useEffectEvent(() => {
    setVisible(false)
  })

  const [position, setPosition] = useState({ top: -9999, left: -9999 })
  const zIndex = useZIndex(visible)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const tooltipContentRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Функция расчета позиции тултипа с auto-flip
  // Same reason as CDropdown's getDropdownPosition: `placement` is all it reads besides refs, so a
  // useCallback makes it listable — and the suppression it replaces was switching off the React
  // Compiler for this component entirely.
  const calculatePosition = useCallback(() => {
    if (triggerRef.current && tooltipContentRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipContentRef.current.getBoundingClientRect()

      let top = 0
      let left = 0
      let effectivePlacement = placement

      // Auto-flip: check if there's enough space for the preferred placement
      const spaceLeft = triggerRect.left
      const spaceRight = window.innerWidth - triggerRect.right
      const spaceTop = triggerRect.top
      const spaceBottom = window.innerHeight - triggerRect.bottom

      if (placement === "left" && spaceLeft < tooltipRect.width + 8 && spaceRight >= tooltipRect.width + 8) {
        effectivePlacement = "right"
      } else if (placement === "right" && spaceRight < tooltipRect.width + 8 && spaceLeft >= tooltipRect.width + 8) {
        effectivePlacement = "left"
      } else if (placement === "top" && spaceTop < tooltipRect.height + 8 && spaceBottom >= tooltipRect.height + 8) {
        effectivePlacement = "bottom"
      } else if (placement === "bottom" && spaceBottom < tooltipRect.height + 8 && spaceTop >= tooltipRect.height + 8) {
        effectivePlacement = "top"
      }

      switch (effectivePlacement) {
        case "top":
          top = triggerRect.top - tooltipRect.height - 8
          left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
          break
        case "right":
          top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
          left = triggerRect.right + 8
          break
        case "bottom":
          top = triggerRect.bottom + 8
          left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
          break
        case "left":
          top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
          left = triggerRect.left - tooltipRect.width - 8
          break
      }

      // Корректировка позиции, чтобы не выходило за границы окна
      top = Math.max(8, Math.min(top, window.innerHeight - tooltipRect.height - 8))
      left = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8))

      return { top, left }
    }
    return { top: 0, left: 0 }
  }, [placement])

  const showTooltip = (immediate = false) => {
    // Если disabled, не показываем tooltip
    if (disabled) return

    // Отменяем скрытие, если оно было запланировано
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    // Рассчитываем позицию сразу
    const newPosition = calculatePosition()
    setPosition(newPosition)

    // Показываем с задержкой для hover, сразу для click
    if (delay > 0 && !immediate) {
      showTimeoutRef.current = setTimeout(() => {
        setVisible(true)
      }, delay)
    } else {
      setVisible(true)
    }
  }

  const hideTooltip = (immediate = false) => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }

    if (immediate || trigger === "click") {
      setVisible(false)
    } else {
      // Для hover добавляем задержку, чтобы мышь успела дойти до тултипа
      hideTimeoutRef.current = setTimeout(() => {
        setVisible(false)
      }, 100)
    }
  }

  /**
   * The timers outlive the component otherwise. A hover that ends in an unmount
   * — a row re-rendered away, a modal closed, a page navigated from — still
   * fires its show or hide and calls setState on a component that is gone. In a
   * test run that lands after the environment is torn down and throws
   * "window is not defined" from React's scheduler, failing a run whose every
   * test passed; in the app it is a wasted render at best.
   */
  useEffect(
    () => () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current)
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    },
    [],
  )

  const cancelHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  const toggleTooltip = () => {
    if (!visible) {
      showTooltip()
    } else {
      hideTooltip(true)
    }
  }

  // Отслеживаем изменение размеров окна и скролл, чтобы пересчитать позицию
  useEffect(() => {
    if (visible) {
      const handlePositionUpdate = () => {
        const newPosition = calculatePosition()
        setPosition(newPosition)
      }

      // Find all scrollable parents and listen to their scroll events
      const scrollableParents: Element[] = []
      let parent = triggerRef.current?.parentElement
      while (parent) {
        const style = getComputedStyle(parent)
        if (
          style.overflow === "auto" ||
          style.overflow === "scroll" ||
          style.overflowY === "auto" ||
          style.overflowY === "scroll" ||
          style.overflowX === "auto" ||
          style.overflowX === "scroll"
        ) {
          scrollableParents.push(parent)
        }
        parent = parent.parentElement
      }

      window.addEventListener("resize", handlePositionUpdate)
      window.addEventListener("scroll", handlePositionUpdate, true)
      scrollableParents.forEach((el) => el.addEventListener("scroll", handlePositionUpdate))

      return () => {
        window.removeEventListener("resize", handlePositionUpdate)
        window.removeEventListener("scroll", handlePositionUpdate, true)
        scrollableParents.forEach((el) => el.removeEventListener("scroll", handlePositionUpdate))
      }
    }
  }, [visible, calculatePosition])

  const getArrowClass = () => {
    const baseClass = "absolute w-2 h-2 rotate-45"
    switch (placement) {
      case "top":
        return `${baseClass} bottom-[-4px] left-1/2 -translate-x-1/2`
      case "right":
        return `${baseClass} left-[-4px] top-1/2 -translate-y-1/2`
      case "bottom":
        return `${baseClass} top-[-4px] left-1/2 -translate-x-1/2`
      case "left":
        return `${baseClass} right-[-4px] top-1/2 -translate-y-1/2`
      default:
        return ""
    }
  }

  // Функция для проверки, находится ли точка внутри элемента
  const isPointInElement = (element: HTMLElement, x: number, y: number) => {
    const rect = element.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  // Escape closes it whichever way it was opened: the overlay below repeats that with the mouse, and
  // a tooltip held open by the keyboard had no way out at all.
  useEffect(() => {
    if (!visible) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeFromOutside()
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [visible])

  // Глобальный захватчик событий клика (только для click trigger)
  useEffect(() => {
    if (trigger !== "click" || !visible) return

    let isListenersActive = false

    const handleGlobalClick = (event: MouseEvent) => {
      if (!isListenersActive) return

      const isInTooltip = tooltipRef.current?.contains(event.target as Node)
      const isInTrigger = triggerRef.current?.contains(event.target as Node)

      // Check if click is inside ANY tooltip (for nested tooltips support)
      const isInAnyTooltip = (event.target as Element).closest("[data-tooltip-content]")

      if (!isInTooltip && !isInTrigger && !isInAnyTooltip) {
        closeFromOutside()

        // Полностью останавливаем событие
        event.stopImmediatePropagation()
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    const handleScroll = () => {
      if (isListenersActive && closeOnScroll) {
        closeFromOutside()
      }
    }

    // Добавляем слушатели с небольшой задержкой, чтобы не поймать событие,
    // которое открыло тултип (особенно важно на touch-устройствах)
    document.addEventListener("click", handleGlobalClick, { capture: true })
    if (closeOnScroll) {
      window.addEventListener("scroll", handleScroll, { capture: true })
    }

    const activationTimeout = setTimeout(() => {
      isListenersActive = true
    }, 50)

    return () => {
      clearTimeout(activationTimeout)
      document.removeEventListener("click", handleGlobalClick, { capture: true })
      if (closeOnScroll) {
        window.removeEventListener("scroll", handleScroll, { capture: true })
      }
    }
  }, [visible, trigger, closeOnScroll])

  const triggerProps =
    trigger === "hover"
      ? {
          onMouseEnter: () => showTooltip(false),
          onMouseLeave: () => hideTooltip(false),
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation()
            // При клике показываем сразу (без задержки) или скрываем если уже открыт
            if (visible) {
              hideTooltip(true)
            } else {
              showTooltip(true)
            }
          },
        }
      : {
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()
            toggleTooltip()
          },
        }

  return (
    <>
      <span
        ref={triggerRef}
        className={`relative inline-block ${className}`}
        {...triggerProps}
      >
        {children}
      </span>

      {/* Скрытый элемент для предварительного расчета размеров тултипа */}
      <div
        ref={tooltipContentRef}
        style={{
          position: "fixed",
          visibility: "hidden",
          top: 0,
          left: 0,
          maxWidth,
          padding: "0.5rem 0.75rem",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
        className="px-3 py-2 text-sm font-medium rounded-lg"
      >
        {content}
      </div>

      {visible &&
        zIndex > 0 &&
        createPortal(
          <>
            {/* Прозрачный оверлей поверх всей страницы для перехвата кликов (только для click trigger) */}
            {trigger === "click" && (
              <div
                // Not a control: with the mouse it repeats the closing Escape now does.
                role="presentation"
                className="fixed inset-0"
                style={{ zIndex, pointerEvents: "all" }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  // Проверяем, был ли клик внутри тултипа или триггера
                  const clickX = e.clientX
                  const clickY = e.clientY

                  if (tooltipRef.current && isPointInElement(tooltipRef.current, clickX, clickY)) {
                    // Клик внутри тултипа, не делаем ничего
                    return
                  }

                  if (triggerRef.current && isPointInElement(triggerRef.current, clickX, clickY)) {
                    // Клик на триггере, не закрываем тултип
                    return
                  }

                  // Клик вне тултипа и триггера, закрываем тултип
                  setVisible(false)
                }}
              />
            )}

            {/* Сам тултип */}
            <div
              // Not a control: the handler only keeps the click inside the bubble.
              role="presentation"
              data-tooltip-content="true"
              onClick={(e) => {
                if (trigger === "click") {
                  e.stopPropagation()
                  e.preventDefault()
                }
              }}
              onMouseEnter={trigger === "hover" ? cancelHide : undefined}
              onMouseLeave={trigger === "hover" ? () => hideTooltip(true) : undefined}
              ref={tooltipRef}
              className={`fixed px-3 py-2 text-sm font-medium rounded-lg transition-opacity duration-200 ${tooltipClassName}`}
              style={{
                zIndex: zIndex + 1,
                top: `${position.top}px`,
                left: `${position.left}px`,
                maxWidth,
                opacity: 1,
                // The light variant is a popover, so it follows the page's theme (shared/theme/theme.css).
                backgroundColor: theme === "light" ? "var(--color-popover)" : "rgba(0, 0, 0, 0.76)",
                color: theme === "light" ? "var(--text-color-gray-900)" : "#ffffff",
                border: theme === "light" ? "1px solid var(--border-color-gray-200)" : "none",
                boxShadow: theme === "light" ? "0 10px 15px -3px rgba(0, 0, 0, 0.1)" : "none",
              }}
            >
              {content}
              {arrow && (
                <span
                  className={getArrowClass()}
                  style={{ backgroundColor: theme === "light" ? "var(--color-popover)" : "rgba(0, 0, 0, 0.76)" }}
                />
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  )
}

export default CTooltip
